import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import {
  ArrowLeft,
  History,
  User,
  Calendar,
  FileText,
  Search,
  Sparkles,
  Phone,
} from "lucide-react";
import MarketplaceEmptyState, { MarketplaceLoadingSpinner } from "../Marketplace/MarketplaceEmptyState";
import { showErrorToast } from "../../../atoms/Utlis/Toast";
import BrandDatePicker from "../../../atoms/Utlis/BrandDatePicker";
import {
  getEquipmentItems,
  getEquipmentItemDetail,
  getEquipmentAssignments,
  getGlobalAssignments,
  EquipmentItem,
} from "../../../../Redux/ApiCalls/Dashboard/EquipmentAPI";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Hooks";
import { getAllMembers } from "../../../../Redux/ApiCalls/Dashboard/MembersAPI";
import {
  PageContainer,
  Hero,
  HeroGlow,
  HeroContent,
  HeroText,
  HeroBadge,
  PageTitle,
  PageSubtitle,
  Panel,
  PanelTop,
  SearchControl,
  StatusBadge,
  CodeText,
  EmptyDash,
  EquipmentInfo,
  EquipmentName,
  EquipmentMeta,
  FooterRow,
  FooterText,
  Pagination,
  PageButton,
  EntriesControl,
  Divider,
  Toolbar,
  FiltersSelectRow,
} from "./EquipmentListPageStyles";
import {
  ListHeader,
  ListTitle,
  CountBadge,
  PanelTableScroll,
  TableWrap,
  PaymentsTable,
} from "../../../atoms/Dashboard/CustomerTransaction/transactionTableStyles";
import {
  BackButton,
  HeroLeft,
  HeroRow,
  Label,
} from "../../../molecules/Dashboard/Member/memberFormStyles";

const Remarks = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.55);
  max-width: 220px;
`;

const MemberCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CallLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  border: 1px solid rgba(49, 166, 106, 0.25);
  background: rgba(49, 166, 106, 0.1);
  color: #31a66a;
  flex-shrink: 0;
  transition: background 0.15s ease, transform 0.15s ease;

  &:hover {
    background: rgba(49, 166, 106, 0.18);
    transform: scale(1.05);
  }
`;

const PeriodRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const PeriodChip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$active ? "transparent" : "rgba(23, 23, 23, 0.1)")};
  background: ${(p) =>
    p.$active
      ? "linear-gradient(144.01deg, rgba(255, 201, 81, 0.9) 0%, rgba(206, 86, 176, 0.9) 100%)"
      : "#fafafa"};
  color: ${(p) => (p.$active ? "#fff" : "rgba(23, 23, 23, 0.68)")};
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: ${(p) => (p.$active ? "0 4px 12px rgba(201, 77, 170, 0.2)" : "none")};

  &:hover {
    border-color: ${(p) => (p.$active ? "transparent" : "rgba(201, 77, 170, 0.25)")};
    color: ${(p) => (p.$active ? "#fff" : "#c94daa")};
  }
`;

const CustomDateRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  padding: 0 22px 4px;
`;

const DateField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 160px;

  ${Label} {
    font-size: 11px;
  }
`;

type PeriodFilter = "all" | "week" | "month" | "custom";

const startOfDay = (d: Date) => {
  const next = new Date(d);
  next.setHours(0, 0, 0, 0);
  return next;
};

const endOfDay = (d: Date) => {
  const next = new Date(d);
  next.setHours(23, 59, 59, 999);
  return next;
};

const startOfWeek = (d: Date) => {
  const next = startOfDay(d);
  const day = next.getDay();
  const diff = day === 0 ? 6 : day - 1; // Monday start
  next.setDate(next.getDate() - diff);
  return next;
};

const startOfMonth = (d: Date) => {
  const next = startOfDay(d);
  next.setDate(1);
  return next;
};

const parseRowDate = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value.includes("T") ? value : `${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toInputDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const QtyStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  padding: 0 22px;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const QtyCard = styled.div`
  padding: 14px 16px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.08);
`;

const QtyLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(23, 23, 23, 0.45);
`;

const QtyValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #171717;
  margin-top: 4px;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  padding: 22px 22px 8px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: rgba(23, 23, 23, 0.6);
`;

const SerialChip = styled.span`
  display: inline-flex;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(53, 183, 200, 0.1);
  color: #1a8a98;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  margin: 2px;
`;

interface AssignmentRow {
  id: string;
  equipment?: number | { id: number; name?: string };
  equipment_id?: number;
  equipment_name?: string;
  team_member_name?: string;
  team_member?: number;
  team_member_phone?: string;
  team_member_country_code?: string;
  phone_number?: string;
  country_code?: string;
  assigned_at?: string;
  expected_return_at?: string;
  returned_at?: string | null;
  return_status?: string;
  remarks?: string;
  serial_numbers?: string[];
  unit_ids?: number[];
}

const formatDisplayDate = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value.includes("T") ? value : `${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const EquipmentHistoryPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isSingleItem = !!id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { members, isMemberFetched } = useAppSelector((state) => state.member);
  const { selectedBranch } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [equipmentMap, setEquipmentMap] = useState<Record<number, EquipmentItem>>({});
  const [singleEquipment, setSingleEquipment] = useState<EquipmentItem | null>(null);
  const [search, setSearch] = useState("");
  const [entries, setEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [period, setPeriod] = useState<PeriodFilter>("all");
  const [customFrom, setCustomFrom] = useState(() => toInputDate(startOfMonth(new Date())));
  const [customTo, setCustomTo] = useState(() => toInputDate(new Date()));

  useEffect(() => {
    dispatch(getAllMembers(selectedBranch?.id || null));
  }, [selectedBranch?.id, dispatch]);

  const memberPhoneMap = useMemo(() => {
    const map: Record<string, string> = {};
    members.forEach((m) => {
      const digits = `${m.country_code || ""}${m.phone_number || ""}`.replace(/[^\d+]/g, "");
      if (digits) map[String(m.id)] = digits;
    });
    return map;
  }, [members]);

  const getMemberPhone = (row: AssignmentRow): string | null => {
    const fromApi =
      row.team_member_phone ||
      row.phone_number ||
      null;
    if (fromApi) {
      const code = row.team_member_country_code || row.country_code || "";
      return `${code}${fromApi}`.replace(/[^\d+]/g, "") || null;
    }
    if (row.team_member != null) {
      return memberPhoneMap[String(row.team_member)] || null;
    }
    return null;
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (isSingleItem && id) {
          const [detail, historyRes] = await Promise.all([
            getEquipmentItemDetail(id),
            getEquipmentAssignments(id, { page_size: 200 }),
          ]);
          setSingleEquipment(detail);
          setEquipmentMap({ [detail.id]: detail });
          const rows: AssignmentRow[] = Array.isArray(historyRes)
            ? historyRes
            : historyRes?.results || historyRes?.data || [];
          setAssignments(rows);
        } else {
          setSingleEquipment(null);
          const [itemsRes, historyRes] = await Promise.all([
            getEquipmentItems({ page_size: 200, branch_id: selectedBranch?.id }),
            getGlobalAssignments({ page_size: 200, branch_id: selectedBranch?.id }),
          ]);
          const items = itemsRes.results || [];
          const map: Record<number, EquipmentItem> = {};
          items.forEach((item) => {
            map[item.id] = item;
          });
          setEquipmentMap(map);
          const rows: AssignmentRow[] = Array.isArray(historyRes)
            ? historyRes
            : historyRes?.results || historyRes?.data || [];
          setAssignments(rows);
        }
      } catch (err: any) {
        showErrorToast(err?.message || "Failed to load assignment history");
        if (isSingleItem) navigate("/dashboard/equipment");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isSingleItem, navigate, selectedBranch?.id]);

  const resolveEquipmentId = (row: AssignmentRow): number | null => {
    if (typeof row.equipment === "number") return row.equipment;
    if (row.equipment && typeof row.equipment === "object") return row.equipment.id;
    if (row.equipment_id) return row.equipment_id;
    if (isSingleItem && singleEquipment) return singleEquipment.id;
    return null;
  };

  const resolveEquipmentName = (row: AssignmentRow): string => {
    if (row.equipment_name) return row.equipment_name;
    if (row.equipment && typeof row.equipment === "object" && row.equipment.name) {
      return row.equipment.name;
    }
    const eqId = resolveEquipmentId(row);
    if (eqId && equipmentMap[eqId]) return equipmentMap[eqId].name;
    if (singleEquipment) return singleEquipment.name;
    return eqId ? `Equipment #${eqId}` : "Unknown equipment";
  };

  const dateRange = useMemo(() => {
    const now = new Date();
    if (period === "week") {
      return { from: startOfWeek(now), to: endOfDay(now) };
    }
    if (period === "month") {
      return { from: startOfMonth(now), to: endOfDay(now) };
    }
    if (period === "custom") {
      const from = customFrom ? startOfDay(new Date(`${customFrom}T00:00:00`)) : null;
      const to = customTo ? endOfDay(new Date(`${customTo}T00:00:00`)) : null;
      return { from, to };
    }
    return { from: null, to: null };
  }, [period, customFrom, customTo]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assignments.filter((row) => {
      const name = resolveEquipmentName(row).toLowerCase();
      const member = (row.team_member_name || "").toLowerCase();
      const serials = (row.serial_numbers || []).join(" ").toLowerCase();
      const remarks = (row.remarks || "").toLowerCase();
      const matchesSearch =
        !q ||
        name.includes(q) ||
        member.includes(q) ||
        serials.includes(q) ||
        remarks.includes(q);

      if (!matchesSearch) return false;

      if (!dateRange.from && !dateRange.to) return true;

      const rowDate =
        parseRowDate(row.assigned_at) ||
        parseRowDate(row.returned_at) ||
        parseRowDate(row.expected_return_at);
      if (!rowDate) return false;
      if (dateRange.from && rowDate < dateRange.from) return false;
      if (dateRange.to && rowDate > dateRange.to) return false;
      return true;
    });
  }, [assignments, search, equipmentMap, singleEquipment, isSingleItem, dateRange]);

  const totalFiltered = filtered.length;
  const totalPages = Math.ceil(totalFiltered / entries) || 1;
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * entries;
    return filtered.slice(start, start + entries);
  }, [filtered, currentPage, entries]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, entries, period, customFrom, customTo]);

  const totalQty = singleEquipment?.total_quantity ?? singleEquipment?.quantity ?? 0;
  const totalAssigned = singleEquipment?.total_assigned ?? 0;
  const remaining = singleEquipment?.remaining_total ?? totalQty - totalAssigned;

  return (
    <PageContainer>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroRow style={{ width: "100%" }}>
            <HeroLeft>
              <BackButton
                type="button"
                onClick={() => navigate("/dashboard/equipment")}
                aria-label="Back to equipment list"
              >
                <ArrowLeft size={18} />
              </BackButton>
              <HeroText>
                <div className="flex flex-col">
                  <HeroBadge>
                    <History size={13} />
                    Assignment history
                  </HeroBadge>
                  <PageTitle className="linear-text">
                    {isSingleItem
                      ? singleEquipment?.name || "Equipment History"
                      : "All Equipment History"}
                  </PageTitle>
                  <PageSubtitle>
                    {isSingleItem
                      ? "Full handout / return timeline for this equipment item."
                      : "Handout and return timeline across your entire gear inventory."}
                  </PageSubtitle>
                </div>
              </HeroText>
            </HeroLeft>
          </HeroRow>
        </HeroContent>
      </Hero>

      <Panel>
        {isSingleItem && singleEquipment && !loading && (
          <>
            <MetaRow>
              <span>
                <Sparkles size={12} style={{ display: "inline", marginRight: 4 }} />
                {singleEquipment.brand}
                {singleEquipment.model ? ` · ${singleEquipment.model}` : ""}
              </span>
              <span>
                {singleEquipment.category_name || `Category #${singleEquipment.category}`}
              </span>
            </MetaRow>
            <QtyStrip>
              <QtyCard>
                <QtyLabel>Total Quantity</QtyLabel>
                <QtyValue>{totalQty}</QtyValue>
              </QtyCard>
              <QtyCard>
                <QtyLabel>Total Assigned</QtyLabel>
                <QtyValue>{totalAssigned}</QtyValue>
              </QtyCard>
              <QtyCard>
                <QtyLabel>Remaining Total</QtyLabel>
                <QtyValue>{remaining}</QtyValue>
              </QtyCard>
            </QtyStrip>
            {(singleEquipment.assigned_serial_numbers?.length ||
              singleEquipment.available_serial_numbers?.length) && (
                <MetaRow>
                  {!!singleEquipment.assigned_serial_numbers?.length && (
                    <div>
                      <strong>Assigned:</strong>{" "}
                      {singleEquipment.assigned_serial_numbers.map((s) => (
                        <SerialChip key={s}>{s}</SerialChip>
                      ))}
                    </div>
                  )}
                  {!!singleEquipment.available_serial_numbers?.length && (
                    <div>
                      <strong>Available:</strong>{" "}
                      {singleEquipment.available_serial_numbers.map((s) => (
                        <SerialChip key={`a-${s}`}>{s}</SerialChip>
                      ))}
                    </div>
                  )}
                </MetaRow>
              )}
          </>
        )}

        <PanelTop>
          <SearchControl>
            <Search size={16} />
            <input
              placeholder={
                isSingleItem
                  ? "Search by member, serial or remarks..."
                  : "Search by equipment, member, serial or remarks..."
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchControl>
          <EntriesControl>
            <span>Show</span>
            <select
              value={entries}
              onChange={(e) => setEntries(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span>entries</span>
          </EntriesControl>
        </PanelTop>

        <Toolbar>
          <FiltersSelectRow>
            <PeriodRow>
              <PeriodChip
                type="button"
                $active={period === "all"}
                onClick={() => setPeriod("all")}
              >
                All
              </PeriodChip>
              <PeriodChip
                type="button"
                $active={period === "week"}
                onClick={() => setPeriod("week")}
              >
                <Calendar size={13} /> This week
              </PeriodChip>
              <PeriodChip
                type="button"
                $active={period === "month"}
                onClick={() => setPeriod("month")}
              >
                <Calendar size={13} /> This month
              </PeriodChip>
              <PeriodChip
                type="button"
                $active={period === "custom"}
                onClick={() => setPeriod("custom")}
              >
                <Calendar size={13} /> Custom
              </PeriodChip>
            </PeriodRow>
          </FiltersSelectRow>
        </Toolbar>

        {period === "custom" && (
          <CustomDateRow>
            <DateField>
              <Label htmlFor="history-from">From</Label>
              <BrandDatePicker
                name="history-from"
                value={customFrom}
                maxDate={customTo || undefined}
                size="compact"
                onChange={(v) => setCustomFrom(v)}
              />
            </DateField>
            <DateField>
              <Label htmlFor="history-to">To</Label>
              <BrandDatePicker
                name="history-to"
                value={customTo}
                minDate={customFrom || undefined}
                size="compact"
                onChange={(v) => setCustomTo(v)}
              />
            </DateField>
          </CustomDateRow>
        )}

        {loading ? (
          <MarketplaceLoadingSpinner padding="60px 22px" />
        ) : filtered.length === 0 ? (
          <MarketplaceEmptyState
            padding="32px 22px 48px"
            icon={<History size={36} strokeWidth={1.5} />}
            title={assignments.length === 0 ? "No assignment history" : "No matching records"}
            description={
              assignments.length === 0
                ? isSingleItem
                  ? "This equipment has not been handed out yet."
                  : "No gear has been handed out yet. Assignments will appear here."
                : period !== "all"
                  ? "No history found for the selected period. Try another filter."
                  : "Nothing matched your search. Try a different keyword."
            }
            actions={
              search || period !== "all"
                ? [
                  {
                    label: "Clear filters",
                    onClick: () => {
                      setSearch("");
                      setPeriod("all");
                    },
                    variant: "secondary" as const,
                  },
                ]
                : undefined
            }
          />
        ) : (
          <div className="flex flex-col gap-4 px-[22px] py-2" >
            <ListHeader>
              <ListTitle>
                {isSingleItem ? "Assignment logs" : "All assignment logs"}
              </ListTitle>
              <CountBadge>
                {totalFiltered} record{totalFiltered !== 1 ? "s" : ""}
              </CountBadge>
            </ListHeader>
            <PanelTableScroll style={{ width: "100%", margin: 0, paddingBottom: 0 }}>
              <TableWrap>
                <PaymentsTable $minWidth={isSingleItem ? 1040 : 1180}>
                  <thead>
                    <tr>
                      {!isSingleItem && <th><span>Equipment</span></th>}
                      <th><span>Member</span></th>
                      <th><span>Assigned</span></th>
                      <th><span>Expected Return</span></th>
                      <th><span>Returned</span></th>
                      <th><span>Status</span></th>
                      <th><span>Serials</span></th>
                      <th><span>Remarks</span></th>
                      <th><span>Action</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((row) => {
                      const status = row.return_status || (row.returned_at ? "returned" : "out");
                      const displayStatus =
                        status === "out" || status === "assigned"
                          ? "Assigned"
                          : status === "partial"
                            ? "Partial Return"
                            : status === "returned"
                              ? "Returned"
                              : status;
                      const eqId = resolveEquipmentId(row);
                      const eq = eqId ? equipmentMap[eqId] : null;
                      const eqName = resolveEquipmentName(row);
                      const phone = getMemberPhone(row);

                      return (
                        <tr key={row.id}>
                          {!isSingleItem && (
                            <td>
                              <EquipmentInfo>
                                <EquipmentName>{eqName}</EquipmentName>
                                {(eq?.brand || eq?.model) && (
                                  <EquipmentMeta>
                                    {eq?.brand}
                                    {eq?.model ? ` · ${eq.model}` : ""}
                                  </EquipmentMeta>
                                )}
                              </EquipmentInfo>
                            </td>
                          )}
                          <td>
                            <MemberCell>
                              <User size={14} />
                              <span>{row.team_member_name || `Member #${row.team_member}`}</span>
                            </MemberCell>
                          </td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <Calendar size={13} />
                              {formatDisplayDate(row.assigned_at) || <EmptyDash>—</EmptyDash>}
                            </div>
                          </td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <Calendar size={13} />
                              {formatDisplayDate(row.expected_return_at) || <EmptyDash>—</EmptyDash>}
                            </div>
                          </td>
                          <td>
                            {formatDisplayDate(row.returned_at) || <EmptyDash>—</EmptyDash>}
                          </td>
                          <td>
                            <StatusBadge
                              $status={
                                displayStatus === "Assigned" ? "Assigned" : "Available"
                              }
                            >
                              {displayStatus}
                            </StatusBadge>
                          </td>
                          <td>
                            {row.serial_numbers?.length ? (
                              row.serial_numbers.map((s) => (
                                <CodeText key={s} style={{ display: "block" }}>
                                  {s}
                                </CodeText>
                              ))
                            ) : (
                              <EmptyDash>—</EmptyDash>
                            )}
                          </td>
                          <td>
                            {row.remarks ? (
                              <Remarks>
                                <FileText size={12} style={{ marginRight: 4 }} />
                                {row.remarks}
                              </Remarks>
                            ) : (
                              <EmptyDash>—</EmptyDash>
                            )}
                          </td>
                          <td>
                            {phone ? (
                              <CallLink
                                href={`tel:${phone}`}
                                title={`Call ${row.team_member_name || "member"}`}
                                aria-label={`Call ${row.team_member_name || "member"}`}
                              >
                                <Phone size={14} />
                              </CallLink>
                            ) : (
                              <EmptyDash>—</EmptyDash>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </PaymentsTable>
              </TableWrap>
            </PanelTableScroll>

            <Divider />

            <FooterRow>
              <FooterText>
                Showing {totalFiltered === 0 ? 0 : (currentPage - 1) * entries + 1} to{" "}
                {Math.min(currentPage * entries, totalFiltered)} of {totalFiltered} entries
              </FooterText>
              <Pagination>
                <PageButton
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  Previous
                </PageButton>
                {Array.from({ length: totalPages }).map((_, idx) => (
                  <PageButton
                    key={idx}
                    $active={currentPage === idx + 1}
                    onClick={() => setCurrentPage(idx + 1)}
                  >
                    {idx + 1}
                  </PageButton>
                ))}
                <PageButton
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  Next
                </PageButton>
              </Pagination>
            </FooterRow>
          </div>
        )}
      </Panel>
    </PageContainer>
  );
};

export default EquipmentHistoryPage;
